/**
 * Gate 1 — Test Strategy.
 *
 * Accordion left nav (section → numbered subsections) + right pane showing
 * only the selected subsection's cards, matching the Figma layout.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { ReactComponent as ClassificationIcon } from "../../asserts/logo/Classification.svg";
import { ReactComponent as DocumentTypeIcon } from "../../asserts/logo/DocumentType.svg";
import { ReactComponent as DocumentOwnerIcon } from "../../asserts/logo/DocumentOwner.svg";
import { ReactComponent as AISystemIcon } from "../../asserts/logo/AI_System.svg";
import { ReactComponent as GeneratedFollowingIcon } from "../../asserts/logo/GeneratedFollowing.svg";
import { ReactComponent as ApprovalStatusIcon } from "../../asserts/logo/ApprovalStatus.svg";
import { ReactComponent as TestStrategyIcon } from "../../asserts/logo/TestStrategyIcon.svg";
import { ReactComponent as TestCasesIcon } from "../../asserts/logo/TestCasesIcon.svg";
import { ReactComponent as TestSuiteIcon } from "../../asserts/logo/TestSuiteIcon.svg";
import { ReactComponent as PlusIcon } from "../../asserts/logo/PlusIcon.svg";
import { ReactComponent as RegenerateIcon } from "../../asserts/logo/RegenerateIcon.svg";
import { ReactComponent as SpinnerIcon } from "../../asserts/logo/SpinnerIcon.svg";
import { ReactComponent as CardEditIcon } from "../../asserts/logo/EditIcon.svg";
import { fetchTestStrategy, generateTestStrategy, saveCustomStrategy, modifyGeneratedItem } from "../../services/testStrategyClient";
import IntakeGateTabsBar from "../../components/intake/IntakeGateTabsBar";
import IntakeHeaderBackButton from "../../components/intake/IntakeHeaderBackButton";
import { GATE_SUBTABS_PL, GATE_SUBTABS_GAP } from "../../components/layout/layoutConstants";
import IntakeSuccessToast from "../../components/intake/IntakeSuccessToast";
import { ReactComponent as ErrorToastIcon } from "../../asserts/logo/error.svg";
import AddCustomStrategyDrawer from "./AddCustomStrategyDrawer";
import ModifiedGeneratedStrategyDrawer from "./ModifiedGeneratedStrategyDrawer";
import { parseItemCards, parseCustomItemCards, updateItemCardBody } from "./testStrategyCards";
import { exportTestStrategyPdf } from "../../utils/exportTestStrategyPdf";

const log = (...args) => console.log("[GATE1.TEST-STRATEGY]", ...args);

const DOC_TABS = [
  { label: "Test Strategies", Icon: TestStrategyIcon },
  { label: "Test Cases", Icon: TestCasesIcon },
  { label: "Test Suite", Icon: TestSuiteIcon },
];
const ACTIVE_GATE = 1;
const TAB_ACTIVE = "#555555";
const TAB_INACTIVE = "#555555";
const TAB_UNDERLINE = "#005BF0";

const META_ICON_SX = { width: 20, height: 20, flexShrink: 0, mt: 0.15, display: "block" };

/**
 * Back-arrow placement — same 24px / 16px ink standard as Gate 0 and Gate 1
 * headers. CSS backs out the 6px hit-target inset and Lato's ~1px capital
 * side bearing. Other header gaps stay 8px.
 */
const BACK_ARROW_INK_LEFT_PX = 24;
const BACK_ARROW_INK_GAP_RIGHT_PX = 16;
const BACK_ARROW_HIT_TARGET_INSET_PX = 6;
const BACK_ARROW_BUTTON_SIZE_PX = 32;
const TITLE_LEFT_SIDE_BEARING_PX = 1;
const HEADER_INSET_LEFT_PX = BACK_ARROW_INK_LEFT_PX - BACK_ARROW_HIT_TARGET_INSET_PX;
const BACK_ARROW_GAP_RIGHT_PX =
  BACK_ARROW_INK_GAP_RIGHT_PX - BACK_ARROW_HIT_TARGET_INSET_PX - TITLE_LEFT_SIDE_BEARING_PX;
const HEADER_ROW_GAP_PX = 8;
const TITLE_NUDGE_LEFT_PX = BACK_ARROW_GAP_RIGHT_PX - HEADER_ROW_GAP_PX;
/**
 * The metadata strip keeps the banner's original 24px inset, so it needs the
 * difference back now that the banner itself is padded for the arrow's ink.
 */
const BANNER_PADDING_X_PX = 24;
const META_ROW_INSET_PX = BANNER_PADDING_X_PX - HEADER_INSET_LEFT_PX;

const FOOTER_BTN_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.04)";
const FOOTER_BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";

/** Figma Button States — Regenerate / Generate Test Cases (Default / Hover / Pressed). */
const REGEN_BG = "linear-gradient(90deg, #1756E5 0%, #8131E2 100%)";
const REGEN_BG_PRESSED = "linear-gradient(90deg, #0A2E99 0%, #521499 100%)";
const REGEN_SHADOW = "0px 2px 4px 0px rgba(38, 26, 102, 0.15)";
const REGEN_SHADOW_HOVER = "0px 6px 18px 2px rgba(89, 26, 178, 0.35)";
const REGEN_SHADOW_PRESSED = "0px 1px 1px 0px rgba(0, 0, 0, 0.05)";
const REGEN_LOADING_BG =
  "linear-gradient(90deg, #1756E5 0%, #1756E5 25%, #8C73FF 50%, #8131E2 75%, #8131E2 100%)";

const footerBtnTextSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  textTransform: "none",
};

const footerGhostBtnSx = {
  ...footerBtnTextSx,
  minWidth: 0,
  width: "fit-content",
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "4px",
  boxSizing: "border-box",
  borderRadius: "4px",
  border: "1px solid #005BF0",
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  color: "#005BF0",
  boxShadow: FOOTER_BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: FOOTER_BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
    boxShadow: FOOTER_BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: FOOTER_BTN_INSET,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    color: "#BBBBBB",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    cursor: "not-allowed",
  },
};

const footerPrimaryBtnSx = {
  "@keyframes regenShimmer": {
    "0%": { backgroundPosition: "100%" },
    "100%": { backgroundPosition: "-100%" },
  },
  "@keyframes regenSpin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  minWidth: 0,
  width: "fit-content",
  height: 40,
  minHeight: 40,
  px: "20px",
  py: "10px",
  gap: "8px",
  boxSizing: "border-box",
  borderRadius: "8px",
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#FFFFFF",
  bgcolor: "transparent",
  backgroundImage: REGEN_BG,
  border: "1px solid transparent",
  boxShadow: REGEN_SHADOW,
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    m: 0,
    "& > *:nth-of-type(1)": { fontSize: 20, width: 20, height: 20 },
  },
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundImage: REGEN_BG,
    boxShadow: REGEN_SHADOW_HOVER,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${REGEN_BG} !important`,
    border: "2px solid #005FCC",
    boxShadow: REGEN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${REGEN_BG} !important`,
    border: "2px solid #005FCC",
    boxShadow: REGEN_SHADOW_HOVER,
  },
  "&:active:not(.Mui-disabled)": {
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${REGEN_BG_PRESSED} !important`,
    border: "1px solid transparent",
    boxShadow: REGEN_SHADOW_PRESSED,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    color: "#BBBBBB",
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    opacity: 1,
    cursor: "not-allowed",
  },
};

function isCustomSection(section) {
  return String(section?.source || "").toLowerCase() === "custom";
}

function isModifiedSection(section) {
  return String(section?.source || "").toLowerCase() === "modified";
}

function sectionBadgeLabel(section) {
  if (isCustomSection(section)) return "Custom";
  if (isModifiedSection(section)) return "Modified";
  return "Generated";
}

function sectionFlagSx(section) {
  const colors = isCustomSection(section)
    ? { bgcolor: "#FFEDD5", color: "#C2410C" }
    : isModifiedSection(section)
      ? { bgcolor: "#E0F2FE", color: "#0369A1" }
      : { bgcolor: "#F5EEFF", color: "#7F2CE8" };
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 24,
    boxSizing: "border-box",
    borderRadius: "999px",
    px: "8px",
    flexShrink: 0,
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    fontSize: 12,
    lineHeight: "100%",
    letterSpacing: "0.2px",
    whiteSpace: "nowrap",
    ...colors,
  };
}

const NAV_TITLE_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      bgcolor: "#000000",
      borderRadius: "4px",
      px: "12px",
      py: "8px",
      fontFamily: "Lato, sans-serif",
      fontWeight: 400,
      fontSize: 12,
      lineHeight: "18px",
      letterSpacing: 0,
      color: "#FFFFFF",
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.25)",
      "& .MuiTooltip-arrow": { color: "#000000" },
    },
  },
};

function TruncatedNavTitle({ title, sx }) {
  const textRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleShouldOpen = () => {
    const el = textRef.current;
    setOpen(!!(el && el.scrollWidth > el.clientWidth));
  };

  return (
    <Tooltip
      title={title}
      arrow
      placement="right"
      open={open}
      onClose={() => setOpen(false)}
      slotProps={NAV_TITLE_TOOLTIP_SLOT_PROPS}
    >
      <Typography
        ref={textRef}
        onMouseEnter={handleShouldOpen}
        onMouseLeave={() => setOpen(false)}
        sx={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
          ...sx,
        }}
      >
        {title}
      </Typography>
    </Tooltip>
  );
}

function NumberedHeading({ number, title, typographySx }) {
  const typeSx = {
    fontFamily: "Lato, sans-serif",
    fontStyle: "normal",
    lineHeight: 1.25,
    letterSpacing: 0,
    ...typographySx,
  };
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "8px", minWidth: 0, width: "100%" }}>
      <Box component="span" sx={{ ...typeSx, flexShrink: 0 }}>
        {number}
      </Box>
      <Box
        component="span"
        sx={{
          ...typeSx,
          flex: 1,
          minWidth: 0,
          overflowWrap: "break-word",
          wordBreak: "break-word",
        }}
      >
        {title}
      </Box>
    </Box>
  );
}

const PRIMARY_BORDERLESS_SX = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#005BF0",
  height: 32,
  minHeight: 32,
  boxSizing: "border-box",
  px: "16px",
  py: "4px",
  gap: "4px",
  borderRadius: "4px",
  border: "2px solid transparent",
  bgcolor: "transparent",
  backgroundColor: "transparent",
  backgroundImage: "none",
  boxShadow: "none",
  verticalAlign: "middle",
  "&:hover:not(.Mui-disabled):not(:disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: "none",
    border: "2px solid transparent",
    color: "#005BF0",
    textDecoration: "underline",
    boxShadow: "none",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: "none !important",
    border: "2px solid #005FCC",
    color: "#005BF0",
    textDecoration: "none",
    boxShadow: "none",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: "none !important",
    border: "2px solid #005FCC",
    color: "#005BF0",
    textDecoration: "none",
  },
  "&:active:not(.Mui-disabled):not(:disabled)": {
    bgcolor: "#F0F6FF !important",
    backgroundColor: "#F0F6FF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    textDecoration: "none",
    boxShadow: "none",
  },
  "&.Mui-disabled, &:disabled": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: "none",
    border: "2px solid transparent",
    color: "#BBBBBB",
    textDecoration: "none",
    boxShadow: "none",
    cursor: "not-allowed",
  },
  "& .MuiTouchRipple-root": { display: "none" },
};

const CARD_EDIT_SX = {
  ...PRIMARY_BORDERLESS_SX,
  minWidth: 55,
  flexShrink: 0,
};

const CUSTOM_CARD_EDIT_SX = {
  ...PRIMARY_BORDERLESS_SX,
  minWidth: 0,
  width: "fit-content",
  flexShrink: 0,
  "& .MuiButton-startIcon": {
    m: 0,
    display: "flex",
    alignItems: "center",
  },
};

const PRIORITY_PILL_SX = {
  Low: { bgcolor: "#F1F5F9", color: "#475569" },
  Medium: { bgcolor: "#FEF3C7", color: "#B45309" },
  High: { bgcolor: "#FFEDD5", color: "#C2410C" },
  Critical: { bgcolor: "#FEE2E2", color: "#B91C1C" },
};

function normalizeFlagLabel(value) {
  const found = ["Critical", "High", "Medium", "Low"].find(
    (label) => label.toLowerCase() === String(value || "").toLowerCase()
  );
  return found || null;
}

function extractContentFlag(title, text) {
  let nextTitle = String(title || "");
  let nextText = String(text || "");
  let flag = null;

  const bracketRe = /\[\s*(Critical|High|Medium|Low)\s*\]/i;
  const trailingRe = /(?:[,;|/—–-]\s*)(Critical|High|Medium|Low)\s*$/i;

  const fromTitleBracket = nextTitle.match(bracketRe);
  if (fromTitleBracket) {
    flag = normalizeFlagLabel(fromTitleBracket[1]);
    nextTitle = nextTitle.replace(bracketRe, "").trim();
  }

  if (!flag) {
    const fromTextBracket = nextText.match(bracketRe);
    if (fromTextBracket) {
      flag = normalizeFlagLabel(fromTextBracket[1]);
      nextText = nextText.replace(bracketRe, "").trim();
    }
  }

  if (!flag) {
    const fromTitleTrail = nextTitle.match(trailingRe);
    if (fromTitleTrail) {
      flag = normalizeFlagLabel(fromTitleTrail[1]);
      nextTitle = nextTitle.slice(0, fromTitleTrail.index).trim().replace(/[,;|/—–-]\s*$/, "").trim();
    }
  }

  if (!flag) {
    const fromTextTrail = nextText.match(trailingRe);
    if (fromTextTrail) {
      flag = normalizeFlagLabel(fromTextTrail[1]);
      nextText = nextText.slice(0, fromTextTrail.index).trim().replace(/[,;|/—–-]\s*$/, "").trim();
    }
  }

  return { title: nextTitle, text: nextText, flag };
}

function PriorityPill({ label }) {
  const colors = PRIORITY_PILL_SX[label];
  if (!colors) return null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        height: 24,
        boxSizing: "border-box",
        px: "8px",
        py: "4px",
        borderRadius: "999px",
        bgcolor: colors.bgcolor,
        color: colors.color,
        fontFamily: "Lato, sans-serif",
        fontWeight: 700,
        fontSize: 11,
        lineHeight: "100%",
        letterSpacing: 0,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
  );
}

function StrategyCard({ title, text, onEdit, empty = false, showEditIcon = false }) {
  const parsed = extractContentFlag(title, text);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        p: "16px",
        borderRadius: "12px",
        border: "1px solid #E6E6E6",
        bgcolor: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        minHeight: 32,
        minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "8px",
            minWidth: 0,
            flex: 1,
          }}
        >
          {parsed.title ? (
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontWeight: 700,
                fontSize: 12,
                lineHeight: "100%",
                letterSpacing: 0,
                color: "#555555",
                minWidth: 0,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {parsed.title}
            </Typography>
          ) : null}
          {parsed.flag ? <PriorityPill label={parsed.flag} /> : null}
        </Box>
        <Button
          size="small"
          disableRipple
          disableElevation
          onClick={onEdit}
          startIcon={
            showEditIcon ? (
              <Box
                component={CardEditIcon}
                sx={{ width: 13, height: 13, display: "block", flexShrink: 0 }}
              />
            ) : undefined
          }
          sx={showEditIcon ? CUSTOM_CARD_EDIT_SX : CARD_EDIT_SX}
        >
          Edit
        </Button>
      </Box>
      <Typography
        sx={{
          fontFamily: "Lato, sans-serif",
          fontWeight: 400,
          fontSize: 13.5,
          lineHeight: 1.6,
          letterSpacing: 0,
          color: empty ? "#98A2B3" : "#383838",
          whiteSpace: "pre-wrap",
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          overflow: "hidden",
          textAlign: "left",
        }}
      >
        {parsed.text}
      </Typography>
    </Box>
  );
}

function MetaItem({ label, value, icon, showDivider = false }) {
  return (
    <Box
      tabIndex={0}
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        display: "flex",
        alignItems: "flex-start",
        gap: { xs: 0.75, lg: 1 },
        px: { xs: 0.75, md: 1, lg: 1.5 },
        py: 1.25,
        borderRight: showDivider ? "1px solid #E6E6E6" : "none",
        "&:focus": { outline: "none" },
        "&:focus-visible": {
          outline: "2px solid #005FCC",
          outlineOffset: "-2px",
        },
      }}
    >
      {icon}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            width: "100%",
            maxWidth: "100%",
            height: 17,
            fontFamily: "Lato, sans-serif",
            fontWeight: 400,
            fontStyle: "normal",
            fontSize: 14,
            lineHeight: "100%",
            letterSpacing: 0,
            color: "#555555",
            mb: 0.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            width: "100%",
            maxWidth: "100%",
            height: 40,
            fontFamily: "Lato, sans-serif",
            fontWeight: 700,
            fontStyle: "normal",
            fontSize: 14,
            lineHeight: "20px",
            letterSpacing: 0,
            color: "#383838",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            wordBreak: "break-word",
          }}
        >
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function CountBadge({ n }) {
  return (
    <Box
      sx={{
        minWidth: 23,
        height: 17,
        px: "8px",
        py: "2px",
        boxSizing: "border-box",
        borderRadius: "10px",
        bgcolor: "#E6E6E6",
        color: "#555555",
        fontFamily: "Lato, sans-serif",
        fontSize: 11,
        fontWeight: 700,
        lineHeight: "100%",
        letterSpacing: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {n}
    </Box>
  );
}

/** Split a subsection body into titled cards — see testStrategyCards.js */
export default function TestStrategyPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const [sections, setSections] = useState([]);
  const [documentMeta, setDocumentMeta] = useState(null);
  const [canGenerate, setCanGenerate] = useState(false);

  const [expandedIdx, setExpandedIdx] = useState(0);
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);
  const [activeItemIdx, setActiveItemIdx] = useState(0);
  const [subsectionFocused, setSubsectionFocused] = useState(false);
  const [customDrawerOpen, setCustomDrawerOpen] = useState(false);
  const [editSubsection, setEditSubsection] = useState(null);
  const [editGenerated, setEditGenerated] = useState(null);
  const [exporting, setExporting] = useState(false);
  const rightPaneRef = useRef(null);

  const selectFirstItem = (secs) => {
    setExpandedIdx(0);
    setActiveSectionIdx(0);
    setActiveItemIdx(0);
    return secs;
  };

  const applyData = (data) => {
    const secs = data?.strategy?.sections || [];
    setSections(secs);
    setDocumentMeta(data?.documentMeta || null);
    setCanGenerate(Boolean(data?.canGenerate));
    selectFirstItem(secs);
  };

  const hasStrategyContent = (data) => {
    const secs = data?.strategy?.sections || [];
    return secs.some((s) => (s.items?.length || 0) > 0 || s.summary);
  };

  const runGenerate = async ({ showSuccessToast = true } = {}) => {
    setError("");
    try {
      setGenerating(true);
      log("POST generate test strategy");
      const res = await generateTestStrategy(projectId);
      if (res?.success) {
        applyData(res.data);
        if (showSuccessToast) {
          const items = (res.data?.strategy?.sections || []).reduce(
            (n, s) => n + (s.items?.length || 0),
            0
          );
          setToast(
            `Test strategy generated — ${items} items across ${(res.data?.strategy?.sections || []).length} sections.`
          );
        }
        return true;
      }
      setError(res?.error || "Generation failed.");
      return false;
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Test strategy generation failed.");
      return false;
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const res = await fetchTestStrategy(projectId);
        if (cancelled || !res?.success) return;

        const data = res.data;
        if (hasStrategyContent(data)) {
          applyData(data);
          return;
        }

        applyData(data);

        if (data?.canGenerate) {
          setLoading(false);
          await runGenerate({ showSuccessToast: false });
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || e.message || "Failed to load test strategy.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const hasContent = useMemo(
    () => sections.some((s) => (s.items?.length || 0) > 0 || s.summary),
    [sections]
  );

  const handleGenerate = () => runGenerate({ showSuccessToast: true });

  const handleViewStrategy = async () => {
    if (exporting) return;
    if (!hasContent) {
      setToast("Generate a test strategy before downloading.");
      return;
    }
    try {
      setExporting(true);
      await exportTestStrategyPdf({ documentMeta, sections });
      setToast("Validation strategy downloaded as PDF.");
    } catch (err) {
      console.error("Test strategy export failed:", err);
      setToast("Failed to download validation strategy. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const activeSection = sections[activeSectionIdx] || null;
  const activeItems = activeSection?.items || [];
  const activeIsCustom = activeSection ? isCustomSection(activeSection) : false;

  const openCustomDrawer = () => {
    setCustomDrawerOpen(true);
  };

  const closeCustomDrawer = () => {
    setCustomDrawerOpen(false);
    setEditSubsection(null);
  };

  const closeEditGenerated = () => {
    setEditGenerated(null);
  };

  const openEditSubsection = (sectionIdx, itemIdx) => {
    const section = sections[sectionIdx];
    const item = section?.items?.[itemIdx];
    if (!section || !item) return;
    setCustomDrawerOpen(false);
    setEditSubsection({ sectionIdx, itemIdx });
  };

  const openEditGeneratedItem = (sectionIdx, itemIdx, cardIdx = null) => {
    const section = sections[sectionIdx];
    const item = section?.items?.[itemIdx];
    if (!section || !item || isCustomSection(section)) return;
    setCustomDrawerOpen(false);
    setEditSubsection(null);
    const cards = parseItemCards(item);
    const card = cardIdx != null ? cards[cardIdx] : cards[0];
    setEditGenerated({
      sectionIdx,
      itemIdx,
      cardIdx: cardIdx != null ? cardIdx : cards.length === 1 ? 0 : null,
      subsectionHeading: item.heading || "",
      cardTitle: card?.title || "",
      initialDescription: card?.text || item.body || "",
    });
  };

  const handleSaveModifiedGenerated = async ({ description }) => {
    if (!editGenerated) return;
    setError("");
    const { sectionIdx, itemIdx, cardIdx } = editGenerated;
    const section = sections[sectionIdx];
    const item = section?.items?.[itemIdx];
    if (!section || !item) return;

    const body =
      cardIdx != null
        ? updateItemCardBody(item, cardIdx, description)
        : description;

    try {
      const res = await modifyGeneratedItem(projectId, {
        sectionId: section.id,
        itemIndex: itemIdx,
        body,
      });
      if (!res?.success) {
        setError(res?.error || "Failed to update generated strategy.");
        return;
      }
      const secs = res.data?.strategy?.sections || [];
      setSections(secs);
      setCanGenerate(Boolean(res.data?.canGenerate));
      setExpandedIdx(sectionIdx);
      setActiveSectionIdx(sectionIdx);
      setActiveItemIdx(itemIdx);
      setSubsectionFocused(true);
      closeEditGenerated();
      setToast("Generated strategy updated.");
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to update generated strategy.");
    }
  };

  const handleSaveCustomStrategy = async (payload) => {
    setError("");
    const wasDescriptionEdit = Boolean(editSubsection);
    try {
      const res = await saveCustomStrategy(projectId, payload);
      if (!res?.success) {
        setError(res?.error || "Failed to save custom strategy.");
        return;
      }
      const secs = res.data?.strategy?.sections || [];
      setSections(secs);
      setCanGenerate(Boolean(res.data?.canGenerate));
      const savedId = res.data?.savedSectionId || payload.id;
      const newIdx = secs.findIndex((s) => s.id === savedId);
      if (newIdx >= 0) {
        setExpandedIdx(newIdx);
        setActiveSectionIdx(newIdx);
        if (wasDescriptionEdit && editSubsection) {
          setActiveItemIdx(editSubsection.itemIdx);
          setSubsectionFocused(true);
        } else {
          setActiveItemIdx(0);
          setSubsectionFocused(false);
        }
      }
      closeCustomDrawer();
      setToast(
        wasDescriptionEdit
          ? "Subsection description updated."
          : `Custom strategy "${payload.title}" saved.`
      );
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to save custom strategy.");
    }
  };

  /** Select a main section — right pane always shows every subsection under it. */
  const selectSection = (i) => {
    setExpandedIdx(i);
    setActiveSectionIdx(i);
    setActiveItemIdx(0);
    setSubsectionFocused(false);
    requestAnimationFrame(() => {
      rightPaneRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const toggleSection = (i) => {
    // Clicking the main section always loads all of its subsections on the right.
    if (activeSectionIdx === i && expandedIdx === i) {
      setExpandedIdx(-1);
      return;
    }
    selectSection(i);
  };

  const selectSubsection = (sectionIdx, itemIdx) => {
    setExpandedIdx(sectionIdx);
    setActiveSectionIdx(sectionIdx);
    setActiveItemIdx(itemIdx);
    setSubsectionFocused(true);
  };

  useEffect(() => {
    if (!subsectionFocused) return;
    const pane = rightPaneRef.current;
    const el = document.getElementById(`ts-sub-${activeSectionIdx}-${activeItemIdx}`);
    if (!pane || !el) return;
    const paneRect = pane.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const top = pane.scrollTop + (elRect.top - paneRect.top) - 16;
    pane.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, [subsectionFocused, activeSectionIdx, activeItemIdx]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#F5F5F5", minWidth: 0 }}>
      <IntakeGateTabsBar
        activeGate={ACTIVE_GATE}
        unlockedUpTo={ACTIVE_GATE}
        onGateClick={(gateIndex) => {
          if (gateIndex === 0) navigate(`/chat/${projectId}`);
          if (gateIndex === 1) navigate(`/gate1/${projectId}`);
        }}
        showBack={false}
        onViewSummary={handleViewStrategy}
        viewSummaryLabel={exporting ? "Downloading…" : "Download Strategy"}
        summaryDisabled={exporting}
      />

      {/* Sub-tabs — Figma: icon + label, active = bold + 3px #005BF0 underline */}
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "space-between",
          pl: GATE_SUBTABS_PL,
          pr: 3,
          height: 50,
          minHeight: 50,
          bgcolor: "#fff",
          borderBottom: "1px solid #E6E6E6",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "stretch", gap: GATE_SUBTABS_GAP }}>
          {DOC_TABS.map((t, i) => {
            const active = i === 0;
            const tabRoutes = {
              "Test Strategies": `/test-strategy/${projectId}`,
              "Test Cases": `/test-cases/${projectId}`,
              "Test Suite": `/test-suites/${projectId}`,
            };
            const clickable = i !== 0;
            const Icon = t.Icon;
            return (
              <Box
                key={t.label}
                onClick={() => clickable && navigate(tabRoutes[t.label])}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  height: "100%",
                  py: "8px",
                  boxSizing: "border-box",
                  cursor: clickable ? "pointer" : "default",
                  color: active ? TAB_ACTIVE : TAB_INACTIVE,
                  "& svg": { width: 17, height: 17, display: "block", flexShrink: 0 },
                  "& svg path": { stroke: "currentColor" },
                  ...(active && {
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: "-8px",
                      right: "-8px",
                      bottom: 0,
                      height: "3px",
                      bgcolor: TAB_UNDERLINE,
                      pointerEvents: "none",
                    },
                  }),
                }}
              >
                <Icon aria-hidden />
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontSize: 14,
                    fontWeight: active ? 700 : 400,
                    fontStyle: "normal",
                    lineHeight: "18px",
                    letterSpacing: 0,
                    color: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Button
            variant="contained"
            size="small"
            disableElevation
            disableRipple
            startIcon={
              generating ? (
                <Box
                  component={SpinnerIcon}
                  sx={{ width: 14, height: 14, display: "block" }}
                />
              ) : (
                <RegenerateIcon />
              )
            }
            disabled={generating || (!hasContent && !canGenerate)}
            onClick={handleGenerate}
            sx={{
              ...footerPrimaryBtnSx,
              backgroundImage: generating ? REGEN_LOADING_BG : REGEN_BG,
              backgroundSize: generating ? "200% 100%" : "auto",
              animation: generating ? "regenShimmer 1.5s ease-in-out infinite" : "none",
              opacity: generating ? 0.6 : 1,
              "& .MuiButton-startIcon": {
                m: 0,
                "& > *:nth-of-type(1)": generating
                  ? { fontSize: 14, width: 14, height: 14 }
                  : { fontSize: 20, width: 20, height: 20 },
                ...(generating && {
                  "& svg": { animation: "regenSpin 1s linear infinite" },
                }),
              },
              "&.Mui-disabled": generating
                ? {
                    color: "#FFFFFF",
                    bgcolor: "transparent",
                    backgroundColor: "transparent",
                    backgroundImage: REGEN_LOADING_BG,
                    backgroundSize: "200% 100%",
                    animation: "regenShimmer 1.5s ease-in-out infinite",
                    opacity: 0.6,
                    border: "1px solid transparent",
                    boxShadow: REGEN_SHADOW,
                    cursor: "default",
                  }
                : footerPrimaryBtnSx["&.Mui-disabled"],
            }}
          >
            {generating ? "Generating..." : hasContent ? "Regenerate" : "Generate Test Strategy"}
          </Button>
        </Box>
      </Box>

      {/* Document header banner */}
      <Box
        sx={{
          pl: `${HEADER_INSET_LEFT_PX}px`,
          pr: 3,
          py: 2,
          boxSizing: "border-box",
          bgcolor: "#fff",
          borderBottom: "1px solid #E6E6E6",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: `${HEADER_ROW_GAP_PX}px`,
            width: "100%",
            minWidth: 0,
            mb: 1.5,
          }}
        >
          <IntakeHeaderBackButton
            onClick={() => navigate(`/gate1/${projectId}`)}
            aria-label="Back"
          />
          <Typography
            sx={{
              ml: `${TITLE_NUDGE_LEFT_PX}px`,
              flex: "1 1 auto",
              minWidth: 0,
              fontSize: 22,
              fontWeight: 800,
              color: "#111827",
              m: 0,
            }}
          >
            {documentMeta?.aiSystem || "Test Strategy"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "stretch",
            width: "100%",
            bgcolor: "#fff",
            overflow: "hidden",
            pl: `${META_ROW_INSET_PX}px`,
            boxSizing: "border-box",
          }}
        >
          <MetaItem
            label="Classification"
            value={documentMeta?.classification}
            icon={<Box component={ClassificationIcon} sx={META_ICON_SX} />}
            showDivider
          />
          <MetaItem
            label="Document Type"
            value={documentMeta?.documentType}
            icon={<Box component={DocumentTypeIcon} sx={META_ICON_SX} />}
            showDivider
          />
          <MetaItem
            label="Document Owner"
            value={documentMeta?.documentOwner}
            icon={<Box component={DocumentOwnerIcon} sx={META_ICON_SX} />}
            showDivider
          />
          <MetaItem
            label="AI System"
            value={documentMeta?.aiSystem}
            icon={<Box component={AISystemIcon} sx={META_ICON_SX} />}
            showDivider
          />
          <MetaItem
            label="Generated Following"
            value={documentMeta?.generatedFollowing}
            icon={<Box component={GeneratedFollowingIcon} sx={META_ICON_SX} />}
            showDivider
          />
          <MetaItem
            label="Approval Status"
            value={documentMeta?.approvalStatus}
            icon={<Box component={ApprovalStatusIcon} sx={META_ICON_SX} />}
          />
        </Box>
      </Box>

      {loading || generating ? (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
          <CircularProgress />
          <Typography sx={{ fontSize: 14, color: "#667085", fontWeight: 600 }}>
            {generating ? "Generating test strategy…" : "Loading test strategy…"}
          </Typography>
        </Box>
      ) : !hasContent ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
          <Box sx={{ textAlign: "center", maxWidth: 460 }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 48, color: "#98A2B3", mb: 1 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#344054", mb: 0.5 }}>
              No test strategy yet
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#667085", mb: 2 }}>
              {canGenerate
                ? "Generate a validation strategy from your app summary, trust weights, risk register, and metrics."
                : "Complete Gate 1 (trust weights, risk register, and metrics) first, then generate the test strategy."}
            </Typography>
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              disabled={generating || !canGenerate}
              onClick={handleGenerate}
              sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#1D4ED8", "&:hover": { bgcolor: "#1E40AF" } }}
            >
              Generate Test Strategy
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              minHeight: 0,
              overflow: "hidden",
              px: 0,
              pb: 0,
              pt: 0,
              gap: 0,
            }}
          >
            {/* Left nav — accordion with subsections */}
            <Box
              sx={{
                width: { xs: "100%", sm: "clamp(240px, 24%, 360px)" },
                maxWidth: { xs: "100%", sm: 360 },
                flex: { xs: "0 0 auto", sm: "0 0 clamp(240px, 24%, 360px)" },
                flexShrink: 0,
                bgcolor: "#FFFFFF",
                borderRight: { xs: "none", sm: "1px solid #E6E6E6" },
                borderBottom: { xs: "1px solid #E6E6E6", sm: "none" },
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                minWidth: 0,
                maxHeight: { xs: 320, sm: "none" },
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  p: "8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0px",
                }}
              >
                {sections.map((s, i) => {
                  const items = s.items || [];
                  const expanded = expandedIdx === i;
                  const sectionActive = activeSectionIdx === i;
                  const headerColor = sectionActive ? "#0046B8" : "#383838";
                  return (
                    <Box key={s.id || i} sx={{ display: "contents" }}>
                      <Box
                        onClick={() => toggleSection(i)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          width: "100%",
                          height: 40,
                          boxSizing: "border-box",
                          px: "8px",
                          py: "8px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          bgcolor: "transparent",
                          minWidth: 0,
                          "&:hover": { bgcolor: "#F9FAFB" },
                        }}
                      >
                        {expanded ? (
                          <KeyboardArrowDownIcon sx={{ fontSize: 18, color: headerColor, flexShrink: 0 }} />
                        ) : (
                          <KeyboardArrowRightIcon sx={{ fontSize: 18, color: headerColor, flexShrink: 0 }} />
                        )}
                        <TruncatedNavTitle
                          title={`${i + 1}. ${s.title}`}
                          sx={{
                            flex: 1,
                            fontFamily: "Lato, sans-serif",
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: "20px",
                            letterSpacing: 0,
                            color: headerColor,
                          }}
                        />
                        <Box component="span" sx={sectionFlagSx(s)}>
                          {sectionBadgeLabel(s)}
                        </Box>
                        <CountBadge n={items.length} />
                      </Box>

                      {expanded &&
                        items.map((it, j) => {
                          const selected = sectionActive && subsectionFocused && activeItemIdx === j;
                          return (
                            <Box
                              key={`${s.id}-${j}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => selectSubsection(i, j)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  selectSubsection(i, j);
                                }
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                width: "100%",
                                height: 40,
                                boxSizing: "border-box",
                                pl: "44px",
                                pr: "8px",
                                py: "8px",
                                borderRadius: 0,
                                cursor: "pointer",
                                overflow: "visible",
                                minWidth: 0,
                                bgcolor: selected ? "#F0F6FF" : "transparent",
                                borderLeft: selected ? "4px solid #0046B8" : "4px solid transparent",
                                "&:hover": {
                                  bgcolor: selected ? "#F0F6FF" : "#E6E6E6",
                                  "& .MuiTypography-root": {
                                    color: selected ? "#005BF0" : "#383838",
                                    fontWeight: 600,
                                  },
                                },
                                "&:focus": {
                                  outline: "none",
                                },
                                "&:focus-visible": {
                                  bgcolor: selected ? "#F0F6FF" : "#FFFFFF",
                                  outline: "2px solid #005CF0",
                                  outlineOffset: "2px",
                                  "& .MuiTypography-root": {
                                    color: selected ? "#005BF0" : "#383838",
                                    fontWeight: 600,
                                  },
                                },
                                "&[aria-disabled='true']": {
                                  bgcolor: "#F7F7F7",
                                  cursor: "not-allowed",
                                  pointerEvents: "none",
                                  "& .MuiTypography-root": {
                                    color: "#ABABAB",
                                    fontWeight: 600,
                                  },
                                },
                              }}
                            >
                              <TruncatedNavTitle
                                title={`${i + 1}.${j + 1} ${it.heading || `Item ${j + 1}`}`}
                                sx={{
                                  flex: 1,
                                  fontFamily: "Lato, sans-serif",
                                  fontSize: 12,
                                  fontWeight: selected ? 600 : 400,
                                  lineHeight: "16px",
                                  letterSpacing: 0,
                                  color: selected ? "#005BF0" : "#555555",
                                }}
                              />
                            </Box>
                          );
                        })}

                      <Box
                        sx={{
                          width: "100%",
                          height: "1px",
                          bgcolor: "#E6E6E6",
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>

              <Box
                sx={{
                  width: 344,
                  maxWidth: "100%",
                  height: 49,
                  boxSizing: "border-box",
                  alignSelf: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="button"
                  type="button"
                onClick={() => openCustomDrawer()}
                sx={{
                    ...PRIMARY_BORDERLESS_SX,
                  display: "flex",
                    flexDirection: "row",
                  alignItems: "center",
                    justifyContent: "center",
                    width: 296,
                    maxWidth: "100%",
                  cursor: "pointer",
                    font: "inherit",
                    whiteSpace: "nowrap",
                    "& svg path": { fill: "currentColor" },
                    "&:hover:not(.Mui-disabled):not(:disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
                      bgcolor: "transparent",
                      backgroundColor: "transparent",
                      backgroundImage: "none",
                      border: "2px solid transparent",
                      color: "#005BF0",
                      textDecoration: "none",
                      boxShadow: "none",
                      "& svg path": { fill: "currentColor" },
                      "& .addCustomStrategyContent": {
                        borderBottom: "1px solid currentColor",
                      },
                    },
                    "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
                      outline: "none",
                      bgcolor: "transparent !important",
                      backgroundColor: "transparent !important",
                      backgroundImage: "none !important",
                      border: "2px solid #005FCC",
                      color: "#005BF0",
                      textDecoration: "none",
                      boxShadow: "none",
                      "& svg path": { fill: "currentColor" },
                      "& .addCustomStrategyContent": {
                        borderBottom: "1px solid transparent",
                      },
                    },
                    "&:active:not(.Mui-disabled):not(:disabled)": {
                      bgcolor: "#F0F6FF !important",
                      backgroundColor: "#F0F6FF !important",
                      backgroundImage: "none !important",
                      border: "1px solid #005BF0",
                      color: "#005BF0",
                      textDecoration: "none",
                      boxShadow: "none",
                      "& svg path": { fill: "currentColor" },
                      "& .addCustomStrategyContent": {
                        borderBottom: "1px solid transparent",
                      },
                    },
                  }}
                >
                  <Box
                    className="addCustomStrategyContent"
                    sx={{
                      display: "inline-flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                      borderBottom: "1px solid transparent",
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "inherit",
                      }}
                    >
                      <Box
                        component={PlusIcon}
                        sx={{ width: 16, height: 16, display: "block", color: "inherit" }}
                      />
                    </Box>
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 700,
                        fontStyle: "normal",
                        fontSize: 13,
                        lineHeight: "24px",
                        letterSpacing: 0,
                        color: "inherit",
                        whiteSpace: "nowrap",
                  }}
                >
                  Add Custom Strategy
                </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Right pane — Content (centering wrapper) + Reading column */}
            <Box
              ref={rightPaneRef}
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                minHeight: 0,
                bgcolor: "#F7F8FA",
                border: "none",
                borderRadius: 0,
                overflowY: "auto",
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                px: { xs: 1.5, sm: 2, xl: "24px" },
                py: 0,
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  minHeight: "100%",
                  flex: "1 0 auto",
                  boxSizing: "border-box",
                  overflow: "hidden",
                  bgcolor: "#FFFFFF",
                  borderTop: "none",
                  borderBottom: "none",
                  borderLeft: "1px solid #E6E6E6",
                  borderRight: "1px solid #E6E6E6",
                  borderRadius: 0,
                  p: { xs: 1.5, sm: 2, xl: "24px" },
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: "12px", xl: "16px" },
              }}
            >
              {customDrawerOpen ? (
                <Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#101828", mb: 2 }}>
                    Custom Test Strategies
                  </Typography>
                  <Box
                    sx={{
                      border: "1px dashed #D0D5DD",
                      borderRadius: 2,
                      minHeight: 280,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 3,
                      py: 4,
                      bgcolor: "#FCFCFD",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Lato, sans-serif",
                        fontSize: 14,
                        color: "#667085",
                        textAlign: "center",
                        maxWidth: 360,
                        lineHeight: 1.6,
                      }}
                    >
                      Use this section to add test strategies that are specific to your organization and not
                      covered by the auto-generated plan.
                    </Typography>
                  </Box>
                </Box>
              ) : activeSection ? (
                <>
                  <NumberedHeading
                    number={`${activeSectionIdx + 1}.`}
                    title={activeSection.title}
                    typographySx={{
                      fontWeight: 900,
                      fontSize: 16,
                      color: "#0046B8",
                    }}
                  />
                  {activeSection.summary && !activeIsCustom && (
                    <Typography sx={{ fontSize: 14, color: "#555", whiteSpace: "pre-wrap" }}>
                      {activeSection.summary}
                    </Typography>
                  )}

                  {activeItems.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: "#98A2B3" }}>
                      No items generated for this section.
                    </Typography>
                  ) : activeIsCustom ? (
                    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      {activeItems.map((it, j) => {
                        const cards = parseCustomItemCards(it);
                        const selected = subsectionFocused && activeItemIdx === j;
                        return (
                          <Box
                            key={j}
                            id={`ts-sub-${activeSectionIdx}-${j}`}
                            onClick={() => selectSubsection(activeSectionIdx, j)}
                            sx={{
                              scrollMarginTop: 16,
                              cursor: "pointer",
                              bgcolor: selected ? "#F0F6FF" : "#FFFFFF",
                              borderTop: j > 0 ? "1px solid #E6E6E6" : "none",
                              borderRadius: 0,
                              minWidth: 0,
                            }}
                          >
                            <Box sx={{ px: "8px", pt: "16px", pb: "16px" }}>
                              <NumberedHeading
                                number={`${activeSectionIdx + 1}.${j + 1}`}
                                title={it.heading || `Item ${j + 1}`}
                                typographySx={{
                                  fontWeight: 700,
                                  fontSize: 14,
                                  color: "#383838",
                                }}
                              />
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", px: "8px", pb: "8px", minWidth: 0 }}>
                              {(cards.length ? cards : [{ title: null, text: it.body || "—" }]).map((card, ci) => (
                                <StrategyCard
                                  key={ci}
                                  title={card.title}
                                  text={card.text}
                                  showEditIcon
                                  onEdit={(e) => {
                                  e.stopPropagation();
                                  openEditSubsection(activeSectionIdx, j);
                                }}
                                />
                              ))}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      {activeItems.map((it, j) => {
                        const cards = parseItemCards(it);
                        const selected = subsectionFocused && activeItemIdx === j;
                        return (
                          <Box
                            key={j}
                            id={`ts-sub-${activeSectionIdx}-${j}`}
                            onClick={() => selectSubsection(activeSectionIdx, j)}
                            sx={{
                              scrollMarginTop: 16,
                              cursor: "pointer",
                              bgcolor: selected ? "#F0F6FF" : "#FFFFFF",
                              borderTop: j > 0 ? "1px solid #E6E6E6" : "none",
                              borderRadius: 0,
                              minWidth: 0,
                            }}
                          >
                            <Box sx={{ px: "8px", pt: "16px", pb: "16px" }}>
                              <NumberedHeading
                                number={`${activeSectionIdx + 1}.${j + 1}`}
                                title={it.heading || `Item ${j + 1}`}
                                typographySx={{
                                  fontWeight: 700,
                                  fontSize: 14,
                                  color: "#383838",
                                }}
                              />
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", px: "8px", pb: "8px", minWidth: 0 }}>
                              {cards.length === 0 ? (
                                <StrategyCard
                                  empty
                                  text="No content for this subsection."
                                  onEdit={(e) => {
                                    e.stopPropagation();
                                    openEditGeneratedItem(activeSectionIdx, j);
                                  }}
                                />
                              ) : (
                                cards.map((card, ci) => (
                                  <StrategyCard
                                    key={ci}
                                    title={card.title}
                                    text={card.text}
                                    onEdit={(e) => {
                                        e.stopPropagation();
                                        openEditGeneratedItem(activeSectionIdx, j, ci);
                                      }}
                                  />
                                ))
                                    )}
                                  </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </>
              ) : null}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flexShrink: 0,
          height: 56,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "24px",
          bgcolor: "#FFFFFF",
          boxShadow: "0px -1px 8px 0px #2122260A",
        }}
      >
        <Button
          disableElevation
          disableRipple
          onClick={() => navigate(`/gate1/${projectId}`)}
          sx={footerGhostBtnSx}
        >
          Back
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Button disableElevation sx={{ ...footerGhostBtnSx, display: "none" }}>
            Save draft
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            disableRipple
            startIcon={<RegenerateIcon />}
            onClick={() =>
              navigate(`/test-cases/${projectId}`, { state: { autoGenerate: true } })
            }
            sx={footerPrimaryBtnSx}
          >
            Generate Test Cases
          </Button>
        </Box>
      </Box>

      <AddCustomStrategyDrawer
        open={customDrawerOpen || Boolean(editSubsection)}
        onClose={closeCustomDrawer}
        onSave={handleSaveCustomStrategy}
        initialSection={editSubsection ? sections[editSubsection.sectionIdx] : null}
        editItemIndex={editSubsection?.itemIdx ?? null}
      />

      <ModifiedGeneratedStrategyDrawer
        open={Boolean(editGenerated)}
        onClose={closeEditGenerated}
        onSave={handleSaveModifiedGenerated}
        subsectionHeading={editGenerated?.subsectionHeading || ""}
        cardTitle={editGenerated?.cardTitle || ""}
        initialDescription={editGenerated?.initialDescription || ""}
      />

      <IntakeSuccessToast
        open={Boolean(toast)}
        message={toast}
        onClose={() => setToast("")}
      />
      {error ? (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            left: 88,
            zIndex: 1600,
            bgcolor: "#1A1A1A",
            color: "#FFFFFF",
            borderRadius: "8px",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            boxShadow: "0 8px 24px rgba(0,0,0,0.28)",
            minWidth: 300,
            maxWidth: 420,
            fontFamily: "Lato, sans-serif",
          }}
        >
          <Box
            component={ErrorToastIcon}
            sx={{ width: 22, height: 22, flexShrink: 0, display: "block" }}
          />
          <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500, lineHeight: 1.4 }}>
            {error}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={() => setError("")}
            aria-label="Dismiss"
            sx={{
              border: "none",
              bgcolor: "transparent",
              color: "#FFFFFF",
              p: 0.25,
              m: 0,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              opacity: 0.85,
              "&:hover": { opacity: 1 },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
